<script setup lang="ts">
	/**
	 * La lista de bloques del correo: se arrastran para reordenar y se apagan con el ojo.
	 *
	 * Es la columna IZQUIERDA del editor. La derecha es la vista previa, que se repinta con
	 * el correo real en cuanto algo cambia aquí.
	 *
	 * Reordenar con una lista y no arrastrando sobre la previa es deliberado: la previa vive
	 * en un iframe con `sandbox` sin `allow-scripts` —porque su HTML trae URLs externas— y
	 * aunque el panel puede leer su DOM, montar manijas flotantes encima significa recalcular
	 * posiciones en cada scroll, resize y repintado. Una lista da el mismo control, se opera
	 * con teclado y no se desalinea nunca.
	 *
	 * Drag-and-drop nativo: son siete filas, una librería pesa más de lo que ahorra.
	 */
	import { ref } from 'vue'

	import {
		EMAIL_BLOCKS,
		type EmailBlockId,
		type EmailBlockState,
		FIXED_EMAIL_BLOCKS,
	} from '@/modules/companies/interfaces/emailSettings'

	const props = defineProps<{
		modelValue: EmailBlockState[]
		disabled?: boolean
		/** Bloques que no aplican a esta variante; se listan en gris y no se pueden prender. */
		unavailable?: EmailBlockId[]
	}>()

	const emit = defineEmits<{ 'update:modelValue': [EmailBlockState[]] }>()

	const draggedIndex = ref<number | null>(null)
	const overIndex = ref<number | null>(null)

	function meta(id: EmailBlockId) {
		return EMAIL_BLOCKS.find((b) => b.value === id)
	}

	function isUnavailable(id: EmailBlockId): boolean {
		return (props.unavailable ?? []).includes(id)
	}

	/** Fijo: abre el correo y no se mueve ni se apaga. Hoy sólo el saludo. */
	function isFixed(id: EmailBlockId): boolean {
		return FIXED_EMAIL_BLOCKS.includes(id)
	}

	/**
	 * Cuántos lugares del principio están tomados por bloques fijos.
	 *
	 * Se cuenta y no se asume 1: es el piso por debajo del cual nadie puede soltar nada, y
	 * el día que haya un segundo bloque fijo esto sigue siendo cierto sin tocar nada más.
	 */
	function fixedCount(): number {
		return props.modelValue.filter((block) => isFixed(block.id)).length
	}

	function move(from: number, to: number) {
		if (props.disabled || from === to) return
		// Ni se saca un fijo de su lugar ni se mete nada por encima de él.
		if (isFixed(props.modelValue[from].id) || to < fixedCount()) return
		const next = [...props.modelValue]
		const [item] = next.splice(from, 1)
		next.splice(to, 0, item)
		emit('update:modelValue', next)
	}

	/**
	 * Un método y no dos sentencias en el atributo: prettier reparte una expresión de varias
	 * sentencias en renglones sin punto y coma, y ahí el compilador de Vue deja de parsearla
	 * —tumbando el build—. Ya pasó antes en este repo.
	 */
	function resetDrag() {
		draggedIndex.value = null
		overIndex.value = null
	}

	function onDrop(index: number) {
		const from = draggedIndex.value
		draggedIndex.value = null
		overIndex.value = null
		if (from !== null) move(from, index)
	}

	function toggleVisible(index: number) {
		const block = props.modelValue[index]
		if (props.disabled || isUnavailable(block.id) || isFixed(block.id)) return
		const next = [...props.modelValue]
		next[index] = { ...block, visible: !block.visible }
		emit('update:modelValue', next)
	}
</script>

<template>
	<ul class="space-y-1.5">
		<li
			v-for="(block, i) in modelValue"
			:key="block.id"
			:draggable="!disabled && !isFixed(block.id)"
			:class="[
				'flex items-center gap-2 rounded-xl border bg-white px-3 py-2 transition',
				overIndex === i ?
					'border-[#253C70] ring-1 ring-[#253C70]'
				:	'border-gray-200',
				draggedIndex === i ? 'opacity-40' : '',
				disabled || isFixed(block.id) ? 'cursor-not-allowed' : (
					'cursor-grab active:cursor-grabbing'
				),
			]"
			@dragstart="draggedIndex = i"
			@dragend="resetDrag"
			@dragover.prevent="overIndex = i"
			@drop.prevent="onDrop(i)"
		>
			<span
				class="select-none text-gray-300"
				:title="isFixed(block.id) ? 'Fijo: siempre abre el correo' : undefined"
				aria-hidden="true"
				>{{ isFixed(block.id) ? '🔒' : '⠿' }}</span
			>

			<div class="min-w-0 flex-1">
				<p
					:class="[
						'truncate text-sm font-medium',
						isUnavailable(block.id) ? 'text-gray-300'
						: block.visible ? 'text-gray-900'
						: 'text-gray-400',
					]"
				>
					{{ meta(block.id)?.label ?? block.id }}
				</p>
				<p class="truncate text-xs text-gray-400">
					{{
						isUnavailable(block.id) ? 'No aplica a esta versión del correo'
						: isFixed(block.id) ?
							'Siempre abre el correo: no se mueve ni se apaga.'
						:	meta(block.id)?.description
					}}
				</p>
			</div>

			<!-- Subir y bajar: el mismo control sin ratón, para teclado y para pantallas chicas -->
			<button
				type="button"
				class="px-1 text-gray-300 hover:text-gray-600 disabled:opacity-30"
				:disabled="
					disabled || i === 0 || isFixed(block.id) || i - 1 < fixedCount()
				"
				title="Subir"
				@click="move(i, i - 1)"
			>
				↑
			</button>
			<button
				type="button"
				class="px-1 text-gray-300 hover:text-gray-600 disabled:opacity-30"
				:disabled="disabled || i === modelValue.length - 1 || isFixed(block.id)"
				title="Bajar"
				@click="move(i, i + 1)"
			>
				↓
			</button>

			<button
				type="button"
				:disabled="disabled || isUnavailable(block.id) || isFixed(block.id)"
				:class="[
					'rounded-lg px-2 py-1 text-xs transition',
					block.visible && !isUnavailable(block.id) ?
						'bg-[#253C70]/10 text-[#253C70]'
					:	'bg-gray-100 text-gray-400',
					disabled || isUnavailable(block.id) || isFixed(block.id) ?
						'cursor-not-allowed'
					:	'',
				]"
				:title="
					isFixed(block.id) ? 'Siempre visible'
					: block.visible ? 'Ocultar del correo'
					: 'Mostrar en el correo'
				"
				@click="toggleVisible(i)"
			>
				{{ block.visible && !isUnavailable(block.id) ? 'Visible' : 'Oculto' }}
			</button>
		</li>
	</ul>
</template>
